import { CompatiblePluginEventType } from "roosterjs";
/** Default inline padding for <ul>/<ol> when source content does not supply one. */ const DEFAULT_LIST_PADDING_LEFT = "40px";
/**
 * Markers for nested <ul> levels, matching the user-agent default in Chrome/Firefox/Safari
 * (disc → circle → square, then cycling back to disc).
 */ const UL_NESTED_MARKERS = [
    "disc",
    "circle",
    "square"
];
/**
 * Rooster plugin that cleans up the paste fragment before RoosterJS commits it to the editor.
 *
 * Two responsibilities, both running on `BeforePaste`:
 *
 *  1. **Strip images.** Pasted images are not supported in our task notes / chat editor surfaces,
 *     so any <img> in the fragment (or in `clipboardData.html`) is removed.
 *
 *  2. **Normalize list formatting.** Pasted lists from sources that rely on browser defaults
 *     (other Planner notes fields, generic web pages, Word Online, etc.) can lose their visible
 *     bullet markers because the pasted markup omits inline `list-style-type` and RoosterJS's
 *     HtmlSanitizer strips implicit `padding-left: 0px` (a default value), leaving markers
 *     rendered outside the visible content box. For every <ul>/<ol> we fill in a sensible
 *     marker (cycling `disc → circle → square` for nested <ul> to match UA defaults; always
 *     `decimal` for <ol>, also matching UA defaults) and a non-zero `padding-left` when the
 *     source did not supply them. Existing inline values — including `list-style-type: none` —
 *     are respected.
 *
 * These two concerns are bundled into one plugin because they share the same lifecycle, the
 * same event, and the same fragment-walking pattern, and every consumer enables them together.
 */ export class RoosterPasteCleanupPlugin {
    getName() {
        return "RoosterPasteCleanupPlugin";
    }
    initialize(_editor) {
    /* no-op */ }
    dispose() {
    /* no-op */ }
    onPluginEvent(event) {
        if (event.eventType !== CompatiblePluginEventType.BeforePaste) {
            return;
        }
        const fragment = event.fragment;
        if (fragment == null) {
            return;
        }
        // 1) Strip images from both the DOM fragment and the raw clipboard HTML.
        event.clipboardData.image = null;
        this.removeImageNodes(fragment);
        if (event.clipboardData.html) {
            event.clipboardData.html = event.clipboardData.html.replace(/<img[^>]*>/g, "");
        }
        // 2) Normalize <ul>/<ol> so the sanitizer's strip-default-styles pass cannot hide markers.
        this.normalizeListNodes(fragment);
    }
    /** Recursively remove <img> elements from the fragment. */ removeImageNodes(node) {
        // Snapshot since removeChild mutates the live childNodes list mid-iteration.
        const snapshot = Array.from(node.childNodes);
        for (const child of snapshot){
            if (child.nodeName?.toLowerCase() === "img") {
                node.removeChild(child);
            } else {
                this.removeImageNodes(child);
            }
        }
    }
    /** Walk the fragment and ensure every <ul>/<ol> has a visible marker and indent. */ normalizeListNodes(root) {
        const isUlOrOl = (node)=>{
            const tag = node.nodeName?.toLowerCase();
            return tag === "ul" || tag === "ol";
        };
        const visit = (node)=>{
            if (isUlOrOl(node)) {
                this.applyDefaultListFormatting(node);
            }
            const children = node.childNodes;
            for(let i = 0; i < children.length; i++){
                visit(children[i]);
            }
        };
        visit(root);
    }
    /**
     * Apply a sensible default list-style-type and padding-left if either is missing,
     * so the sanitizer's default-value stripping does not erase the visible bullet.
     */ applyDefaultListFormatting(listElement) {
        const tag = listElement.nodeName.toLowerCase();
        const defaultMarker = tag === "ol" ? "decimal" : UL_NESTED_MARKERS[this.countSameTagAncestors(listElement, tag) % UL_NESTED_MARKERS.length];
        const existingMarker = listElement.style.listStyleType?.trim().toLowerCase();
        if (!existingMarker) {
            listElement.style.listStyleType = defaultMarker;
        }
        const existingPadding = listElement.style.paddingLeft?.trim();
        if (!existingPadding || existingPadding === "0px" || existingPadding === "0") {
            listElement.style.paddingLeft = DEFAULT_LIST_PADDING_LEFT;
        }
    }
    /**
     * Count ancestor elements that match the given lowercased tag name. Used to derive
     * nesting depth so we can mirror the browser's nested-marker cycle for <ul>.
     */ countSameTagAncestors(element, tag) {
        let depth = 0;
        for(let parent = element.parentElement; parent != null; parent = parent.parentElement){
            if (parent.nodeName.toLowerCase() === tag) {
                depth++;
            }
        }
        return depth;
    }
}
